import React from 'react';
import { motion } from 'framer-motion';
import { 
  UserPlus, 
  Target, 
  TrendingUp, 
  Trophy,
  ArrowRight
} from 'lucide-react';

const HowItWorks: React.FC = () => {
  const steps = [
    {
      icon: UserPlus,
      title: "Sign Up & Setup",
      description: "Create your account in seconds. Set up your profile and connect your financial accounts securely.",
      color: "primary"
    },
    {
      icon: Target,
      title: "Define Your Goals",
      description: "Set specific, measurable financial goals. Choose from templates or create custom goals.",
      color: "accent"
    },
    {
      icon: TrendingUp,
      title: "Track Progress",
      description: "Monitor your progress with real-time updates, charts, and milestone celebrations.",
      color: "success"
    },
    {
      icon: Trophy,
      title: "Achieve Success",
      description: "Reach your goals and celebrate your achievements. Share your success with friends and family.",
      color: "warning"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-textBase mb-6">
            How It Works in
            <span className="gradient-text"> 4 Simple Steps</span>
          </h2>
          <p className="text-xl text-grey-600 max-w-3xl mx-auto">
            Getting started with Financial Goal Tracker is easy. Follow these simple steps 
            to begin your journey towards financial success.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-accent to-success transform -translate-y-1/2 z-0"></div>
          
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-4">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="relative z-10 text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                {/* Step Number */}
                <div className="w-16 h-16 bg-white border-4 border-primary rounded-full flex items-center justify-center mx-auto mb-6 shadow-brand">
                  <span className="text-2xl font-bold text-primary">{index + 1}</span>
                </div>

                {/* Icon */}
                <motion.div
                  className={`w-20 h-20 rounded-full bg-${step.color}/10 flex items-center justify-center mx-auto mb-6`}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                >
                  <step.icon className={`w-10 h-10 text-${step.color}`} />
                </motion.div>

                {/* Content */}
                <div className="bg-white rounded-brand-lg p-6 shadow-brand">
                  <h3 className="text-xl font-semibold text-textBase mb-3">
                    {step.title}
                  </h3>
                  <p className="text-grey-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Arrow (except for last item) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-primary" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Demo Section */}
        <motion.div
          className="mt-20 bg-gradient-to-r from-primary to-primary-dark rounded-brand-lg p-8 text-white text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h3 className="text-3xl font-bold mb-4">
            See It In Action
          </h3>
          <p className="text-xl mb-6 opacity-90">
            Watch a quick demo of how My Financial Goal Tracker helps you achieve your financial goals.
          </p>
          <motion.button
            className="bg-white text-primary px-8 py-3 rounded-brand font-semibold hover:bg-grey-100 transition-colors duration-200"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Watch Demo
          </motion.button>
        </motion.div>

        {/* Benefits */}
        <motion.div
          className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-center">
            <div className="text-4xl font-bold text-primary mb-2">2 Minutes</div>
            <div className="text-grey-600">Setup Time</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-primary mb-2">24/7</div>
            <div className="text-grey-600">Access Anywhere</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-primary mb-2">100%</div>
            <div className="text-grey-600">Secure & Private</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks; 